import React, { useEffect, useState } from 'react';
import {
    Card, CardContent, Divider, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Stack, CardHeader, Checkbox,
    Menu, MenuItem, Button, Dialog, DialogTitle, DialogContent,
    DialogActions, Typography, Tooltip, IconButton, Box, TextField
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import moment from 'moment';

import { api } from "../../../services/Main/Api";
import { getCondoId } from "../../../services/Main/AuthStorage";
import PaginationPage from "../../../components/PaginationPage/PaginationPage";
import { useSnackbar } from "notistack";
import TemplateModalForm from "./TemplateForm";

// ===== Fallback local (se a API falhar) =====
const FALLBACK_TAGS = [
    { campo: 'Nome do cliente', tag: '${nome-cliente}' },
    { campo: 'CPF', tag: '${cpf-cliente}' },
    { campo: 'CNPJ', tag: '${cnpj-cliente}' },
    { campo: 'Endereço', tag: '${endereco-cliente}' },
    { campo: 'Valor do contrato', tag: '${valor}' },
    { campo: 'Taxa Administrativa', tag: '${taxa-administrativa}' },
    { campo: 'Taxa de intermediação', tag: '${taxa-intermediacao}' },
    { campo: 'Testemunha 1', tag: '${testemunha-1}' },
    { campo: 'Testemunha 2', tag: '${testemunha-2}' },
    { campo: 'Testemunha 3', tag: '${testemunha-3}' },
    { campo: 'Testemunha 4', tag: '${testemunha-4}' },
];

const TemplateList = () => {
    const [templates, setTemplates] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    // Modal de instruções
    const [tagsOpen, setTagsOpen] = useState(false);
    const [filter, setFilter] = useState('');
    const [tags, setTags] = useState([]);           // vindo da API normalizada [{campo, tag}]
    const [tagsLoading, setTagsLoading] = useState(false);

    const { enqueueSnackbar } = useSnackbar();

    // Carrega templates
    useEffect(() => {
        api.get("sign/template/", {
            params: { condo_id: getCondoId(), page, page_size: 50 }
        }).then((res) => {
            setTemplates(res.data);
            setSelectedIds([]);
        }).finally(() => setLoading(false));
    }, [page]);

    // Busca tags ao abrir a modal (com cache em memória)
    useEffect(() => {
        const fetchTags = async () => {
            setTagsLoading(true);
            try {
                const res = await api.get("sign/template/tags/", {
                    params: { condo_id: getCondoId(), page_size: 200 }
                });

                const data = res.data?.results || [];
                const normalized = data.map((row) => {
                    const pretty = row.name
                        .replace(/-/g, ' ')
                        .replace(/\b\w/g, (l) => l.toUpperCase()); // Capitaliza cada palavra
                    return {
                        campo: (row.description && row.description.trim()) ? row.description : pretty,
                        tag: `\${${row.name}}`
                    };
                }).filter(r => r.campo && r.tag);

                setTags(normalized.length ? normalized : FALLBACK_TAGS);
            } catch (e) {
                setTags(FALLBACK_TAGS);
                enqueueSnackbar('Não foi possível carregar as tags do servidor. Usando lista padrão.', { variant: 'warning' });
            } finally {
                setTagsLoading(false);
            }
        };

        if (tagsOpen && tags.length === 0 && !tagsLoading) fetchTags();
    }, [tagsOpen, tags.length, tagsLoading, enqueueSnackbar]);

    // Helpers seleção
    const handleSelectAll = (event) => {
        const all = templates?.results?.map((t) => t.id) || [];
        setSelectedIds(event.target.checked ? all : []);
    };

    const handleSelectOne = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
        );
    };

    const isSelected = (id) => selectedIds.includes(id);
    const allSelected = templates.results?.length > 0 && selectedIds.length === templates.results.length;

    // Menu ações
    const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleDeleteClick = () => { setConfirmOpen(true); handleMenuClose(); };

    // Delete
    const handleDeleteConfirmed = async () => {
        try {
            await Promise.all(selectedIds.map((id) => api.delete(`sign/template/${id}/`)));
            setTemplates((prev) => ({
                ...prev,
                results: prev.results.filter((t) => !selectedIds.includes(t.id))
            }));
            enqueueSnackbar("Template(s) deletado(s) com sucesso!", { variant: "success" });
        } catch (error) {
            console.error(error);
            enqueueSnackbar("Erro ao deletar templates", { variant: "error" });
        } finally {
            setSelectedIds([]);
            setConfirmOpen(false);
        }
    };

    // Create
    const handleSubmitNewTemplate = async (formData) => {
        try {
            const res = await api.post("sign/template/", formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            setTemplates((prev) => ({
                ...prev,
                results: [...(prev.results || []), res.data]
            }));
            enqueueSnackbar("Template criado com sucesso!", { variant: "success" });
        } catch (error) {
            console.error(error);
            enqueueSnackbar("Erro ao criar template", { variant: "error" });
        }
    };

    // Filtro de tags
    const dataset = tags.length ? tags : FALLBACK_TAGS;
    const filteredTags = dataset.filter((row) => {
        const f = filter.toLowerCase();
        return (row.campo || '').toLowerCase().includes(f) || (row.tag || '').toLowerCase().includes(f);
    });

    // Copiar
    const copy = async (value) => {
        try {
            await navigator.clipboard.writeText(value);
            enqueueSnackbar('Tag copiada!', { variant: 'info' });
        } catch {
            enqueueSnackbar('Não foi possível copiar.', { variant: 'warning' });
        }
    };

    return (
        <>
            <Card sx={{ margin: 2 }}>
                <CardHeader title={`Modelo de contrato (${templates?.count || 0})`} />
                <Divider sx={{ mb: 2 }} />

                <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setDialogOpen(true)}
                    >
                        Criar modelo
                    </Button>

                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<KeyboardArrowDownIcon />}
                        onClick={handleMenuClick}
                        disabled={selectedIds.length === 0}
                    >
                        Ações {selectedIds.length > 0 ? `(${selectedIds.length})` : ''}
                    </Button>

                    <Tooltip title="Ver instruções e tags disponíveis para usar nos templates">
                        <Button
                            variant="text"
                            size="small"
                            startIcon={<InfoOutlinedIcon />}
                            onClick={() => setTagsOpen(true)}
                        >
                            Instruções de Tags
                        </Button>
                    </Tooltip>

                    <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
                        <MenuItem onClick={handleDeleteClick}>
                            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                            Excluir selecionados
                        </MenuItem>
                    </Menu>
                </Stack>

                <CardContent>
                    {loading ? (
                        <Typography variant="body2">Carregando templates...</Typography>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox checked={allSelected} onChange={handleSelectAll} />
                                        </TableCell>
                                        <TableCell><strong>Nome do Modelo</strong></TableCell>
                                        <TableCell><strong>Criado em</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {templates?.results?.map((template) => (
                                        <TableRow key={template.id} hover>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={isSelected(template.id)}
                                                    onChange={() => handleSelectOne(template.id)}
                                                />
                                            </TableCell>
                                            <TableCell>{template.name}</TableCell>
                                            <TableCell>
                                                {moment(template.created_at).format("DD/MM/YYYY [às] HH:mm")}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {templates?.results?.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                Nenhum template encontrado.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            <PaginationPage data={{ ...templates, page }} onChange={setPage} />

            {/* Modal confirmar exclusão */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>
                        Deseja realmente deletar {selectedIds.length} template{selectedIds.length > 1 ? 's' : ''}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
                    <Button onClick={handleDeleteConfirmed} color="error" variant="contained">
                        Deletar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal Instruções de Tags */}
            <Dialog open={tagsOpen} onClose={() => setTagsOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Instruções de Tags</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Use as tags abaixo exatamente como estão no conteúdo do modelo. Elas serão substituídas automaticamente pelos dados do sistema.
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Filtrar por campo ou tag..."
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        />
                    </Box>

                    {tagsLoading ? (
                        <Typography variant="body2">Carregando tags…</Typography>
                    ) : (
                        <TableContainer>
                            <Table size="small" aria-label="Tabela de tags permitidas">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Campo</strong></TableCell>
                                        <TableCell><strong>Tag</strong></TableCell>
                                        <TableCell align="center"><strong>Ações</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredTags.map((row) => (
                                        <TableRow key={row.tag} hover>
                                            <TableCell>{row.campo}</TableCell>
                                            <TableCell>
                                                <Typography component="span" fontFamily="mono">{row.tag}</Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Copiar tag">
                                                    <IconButton size="small" onClick={() => copy(row.tag)}>
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {filteredTags.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                Nenhuma tag encontrada para esse filtro.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            Observação: mantenha a grafia sem acentos nas tags (ex.: <b>{'${taxa-intermediacao}'}</b>).
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTagsOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            <TemplateModalForm
                isOpen={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSubmit={handleSubmitNewTemplate}
            />
        </>
    );
};

export default TemplateList;
